import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { mapAndCacheArrayElements } from '@sama/rxjs-core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { PushAndTrackService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/push-and-track.service';
import { ShapeSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { getShapesForEventTracking } from '../../../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import {
  getShapeForEventTracking,
  ShapeState,
} from '../../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { BrowserControlService } from '../../../../../../../cross-team-shared/misc-services/browser-control.service';
import {
  ShapeGroupState,
  ShapeOrGroupOrderItem,
} from '../../../../../../../cross-team-shared/state/shape-group-state';
import { reorderInStore } from '../../../../../../../shared/misc-utilities/drag-utils';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';
import { ShapeListPanelService } from '../objectless-shape-list/shape-list-panel.service';

@Component({
  selector: 'h3-shape-group',
  templateUrl: './shape-group.component.html',
  styleUrls: ['./shape-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShapeGroupComponent
  extends DirectiveSuperclass
  implements OnInit, OnChanges
{
  @Input({ required: true }) groupIndex!: number;

  @Input() hoverButtonTemplate?: TemplateRef<unknown>;
  @Input() staticButtonTemplate?: TemplateRef<unknown>;
  @Output() newlySelected = new EventEmitter<void>();

  private readonly outputAnnotationInfoStore = inject(
    outputAnnotationInfoStoreToken,
  );
  private readonly shapesStore = this.outputAnnotationInfoStore('shapes');
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  shapeStores$!: Observable<Array<Store<ShapeState>>>;
  readOnly$: Observable<boolean | undefined>;
  isPanelExpanded$!: Observable<boolean>;
  groupStore!: Store<ShapeGroupState>;

  constructor(
    private browserControlService: BrowserControlService,
    private store: AbstractAnnotationStore,
    private pushAndTrackService: PushAndTrackService,
    private selectionService: ShapeSelectionService,
    private shapeSelectionService: ShapeSelectionService,
    private shapeListPanelService: ShapeListPanelService,
    injector: Injector,
  ) {
    super(injector);
    this.readOnly$ = this.taskWorkspaceStore('readOnly').$;
  }

  ngOnInit() {
    this.setSubscriptionToSelection();
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    this.groupStore = this.outputAnnotationInfoStore('groups')(this.groupIndex);
    // @ts-ignore -- fix me
    this.shapeStores$ = this.getShapeStores$();
    // @ts-ignore -- fix me
    this.isPanelExpanded$ = this.shapeListPanelService.isGroupPanelExpanded$(
      this.groupIndex,
    );
  }

  modifyShapeSelection(event: MouseEvent) {
    const target = event.target as Element;
    if (target.closest('h3-shape-list-shape')) {
      // Don't do group actions when clicking on a shape nested within a group.
      // This is particularly relevant in read-only when CDK dragging is
      // disabled and doesn't stop propagation of events.
      return;
    }

    const groupIndex = this.groupStore.state().index;

    const modifierDown =
      event[this.browserControlService.getPlatformModifier()];
    if (modifierDown) {
      this.selectionService.toggleSelection(
        this.groupStore.state().shapeIndexes,
        { source: 'shape_list_group' },
      );
    } else {
      this.shapeListPanelService.toggleGroupPanelExpanded(groupIndex);
    }
  }

  reorder(event: CdkDragDrop<Store<ShapeState>>) {
    const { previousPosition, newPosition } = reorderInStore(
      this.groupStore('shapeIndexes'),
      event,
    );
    this.pushAndTrackService.pushAndTrack({
      id: 'reorder_shapes',
      bucket: 'adjustment',
      is_group: false,
      is_within_group: true,
      shapes: [getShapeForEventTracking(event.item.data.state())],
      previous_position: previousPosition,
      new_position: newPosition,
    });
  }

  ungroup(event: MouseEvent) {
    event.stopPropagation();
    const groupToDelete = this.groupStore.state();

    this.store.batch(() => {
      this.outputAnnotationInfoStore('shapeAndGroupOrder').setUsing(
        this.replaceGroupWithShapeOrderItems,
        groupToDelete,
      );

      this.groupStore.delete();
      this.selectionService.setSelection(groupToDelete.shapeIndexes);
    });

    const shapesForEventTracking = getShapesForEventTracking(
      // @ts-ignore -- fix me
      this.shapesStore.state(),
      groupToDelete.shapeIndexes,
    );
    this.pushAndTrackService.pushAndTrack({
      id: 'ungroup_shapes',
      bucket: 'tag_selection',
      shapes: shapesForEventTracking,
      group_type: groupToDelete.type,
    });
  }

  dragStarted(shapeStore: Store<ShapeState>) {
    this.shapeListPanelService.setShapePanelState(
      shapeStore.state().index,
      false,
    );
  }

  private replaceGroupWithShapeOrderItems(
    order: ShapeOrGroupOrderItem[],
    groupToDelete: ShapeGroupState,
  ) {
    const currentOrder = [...order];
    const groupOrderIndex = currentOrder.findIndex(
      (item) => item.type === 'group' && item.index === groupToDelete.index,
    );
    const shapeItems = groupToDelete.shapeIndexes.map<ShapeOrGroupOrderItem>(
      (shapeIndex) => ({
        type: 'shape',
        index: shapeIndex,
      }),
    );

    currentOrder.splice(groupOrderIndex, 1, ...shapeItems);
    return currentOrder;
  }

  private setSubscriptionToSelection() {
    this.subscribeTo(
      combineLatest([
        this.getInput$('groupIndex').pipe(
          switchMap(() =>
            this.groupStore('shapeIndexes').$.pipe(filter(isDefined)),
          ),
        ),
        this.shapeSelectionService.getSelectionForOutput$(),
      ]).pipe(
        // @ts-ignore -- fix me
        map(([shapeIndexes, selections]) =>
          // fix me if you change this code

          // @ts-ignore -- fix me
          shapeIndexes.filter((shapeIndex) => selections[shapeIndex]),
        ),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      ),
      (selectedShapesInGroup) => {
        const shouldExpand = !!selectedShapesInGroup.length;
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        const groupIndex = this.groupStore.state()?.index;
        if (groupIndex) {
          this.shapeListPanelService.setGroupPanelState(
            groupIndex,
            shouldExpand,
          );
        }
      },
    );
  }

  private getShapeStores$() {
    return this.groupStore.$.pipe(
      filter(isDefined),
      map((group) => [...group.shapeIndexes].reverse()),
      mapAndCacheArrayElements(
        (shapeIndex) => shapeIndex.toString(),
        (shapeIndex) => this.shapesStore(shapeIndex),
      ),
    );
  }
}
